import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from 'vqp-common';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Icon } from '../common';

const ModalContent = styled.div`
  padding: 24px;
  min-width: 420px;
  max-width: 560px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.colors?.textBold || '#333'};
  margin: 0 0 8px 0;
`;

const Hint = styled.p`
  font-size: 13px;
  color: ${(p) => p.theme.colors?.textPrimary || '#666'};
  margin: 0 0 16px 0;
`;

const List = styled.div`
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};
  border-radius: 8px;
  background: ${(p) => p.theme.colors?.lightGrey || '#fafafa'};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: white;
  border-bottom: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};
  box-shadow: ${(p) => (p.$isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none')};

  &:last-child {
    border-bottom: none;
  }
`;

const DragHandle = styled.span`
  display: inline-flex;
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const OrderBadge = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.theme.colors?.textPrimary || '#666'};
  background: ${(p) => p.theme.colors?.lightGrey || '#eee'};
  min-width: 28px;
  text-align: center;
  padding: 4px 8px;
  border-radius: 12px;
  flex-shrink: 0;
`;

const CategoryName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => p.theme.colors?.textBold || '#333'};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => p.theme.colors?.lineGrey || '#e8e8e8'};

  & > button {
    min-width: 100px;
    height: 36px;
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: ${(p) => p.theme.colors?.textPrimary || '#666'};
`;

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result.map((item, index) => ({
    ...item,
    order: index + 1,
  }));
};

export const OrganizeMenuModal = ({ categories, isOpen, onClose, onSave, loading }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (isOpen && categories) {
      setItems(
        categories.map((cat, index) => ({
          id: cat.id,
          name: cat.name,
          order: cat.order != null ? cat.order : index + 1,
        }))
      );
    }
  }, [isOpen, categories]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    setItems(reorder(items, result.source.index, result.destination.index));
  };

  const handleSave = () => {
    if (!items.length || loading) return;
    onSave(items);
  };

  return (
    <Modal isOpen={isOpen} onClose={loading ? undefined : onClose} boxWidth="560px">
      <ModalContent>
        <ModalTitle>Organizar cardápio</ModalTitle>
        <Hint>Arraste as categorias para definir a ordem de exibição.</Hint>

        {items.length === 0 ? (
          <EmptyState>Nenhuma categoria para organizar.</EmptyState>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="organize-categories">
              {(provided) => (
                <List ref={provided.innerRef} {...provided.droppableProps}>
                  {items.map((cat, index) => (
                    <Draggable
                      key={String(cat.id)}
                      draggableId={`category-${cat.id}`}
                      index={index}
                      isDragDisabled={loading}
                    >
                      {(dragProvided, snapshot) => (
                        <Row
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          $isDragging={snapshot.isDragging}
                        >
                          <DragHandle {...dragProvided.dragHandleProps}>
                            <Icon name="drag" width="16px" height="16px" />
                          </DragHandle>
                          <OrderBadge>{cat.order}</OrderBadge>
                          <CategoryName title={cat.name}>{cat.name}</CategoryName>
                        </Row>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}

        <Actions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            className="primary"
            onClick={handleSave}
            disabled={loading || items.length === 0}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Actions>
      </ModalContent>
    </Modal>
  );
};
